import { trackFeatureUsage } from './analytics';

/**
 * Check if an element should be excluded from the element mapping
 * @param {Element} element - DOM element to check
 * @returns {boolean} True if element should be excluded
 */
function shouldExcludeElement(element) {
  // Exclude VexFlow musical notation elements
  if (element.tagName === 'SVG') {
    // Check if this SVG is inside a sheet music container
    const parent = element.closest('.aisheetnovel-sheet-music, .sheet-music-container, .sheet-music-section');
    if (parent) return true;
  }
  
  // Exclude SVG child elements that are part of musical notation
  if (['g', 'path', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'rect', 'text', 'use'].includes(element.tagName.toLowerCase())) {
    // Check if we're inside a VexFlow SVG
    const svgParent = element.closest('svg');
    if (svgParent) {
      // Check if the SVG is inside a sheet music container
      const sheetMusicParent = svgParent.closest('.aisheetnovel-sheet-music, .sheet-music-container, .sheet-music-section');
      if (sheetMusicParent) return true;
      
      // Check for VexFlow-specific classes or attributes
      if (svgParent.classList.contains('vexflow') || 
          svgParent.querySelector('[class*="vf-"]') ||
          element.classList.contains('vf-note') ||
          element.classList.contains('vf-stave') ||
          element.classList.contains('vf-stem') ||
          element.classList.contains('vf-notehead') ||
          element.getAttribute('class')?.includes('vf-')) {
        return true;
      }
    }
  }
  
  // Exclude elements with VexFlow-specific classes
  if (element.classList.contains('vf-note') ||
      element.classList.contains('vf-stave') ||
      element.classList.contains('vf-stem') ||
      element.classList.contains('vf-notehead') ||
      Array.from(element.classList).some(cls => cls.startsWith('vf-'))) {
    return true;
  }
  
  return false;
}

/**
 * Captures the positions and detailed styling of all visible DOM elements on the current page and stores them in sessionStorage.
 * @returns {void}
 */
export const mapElementPositions = () => {
  const elements = document.querySelectorAll('*');
  const timestamp = new Date().toISOString();
  const pageUrl = window.location.href;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  const elementData = [];
  let excludedCount = 0;

  // Function to get ancestry of an element
  function getAncestry(element, depth = 3) {
    let ancestry = [];
    let current = element;
    let count = 0;
    while (current && current.tagName && count < depth) {
      const tag = current.tagName.toLowerCase();
      const id = current.id ? `#${current.id}` : '';
      const classes = current.classList.length > 0 ? `.${Array.from(current.classList).join('.')}` : '';
      ancestry.unshift(`${tag}${classes}${id}`);
      current = current.parentElement;
      count++;
    }
    return ancestry.join(' > ');
  }

  // Function to check if element is off-screen
  function isOffScreen(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.bottom < 0 ||
      rect.right < 0 ||
      rect.left > window.innerWidth ||
      rect.top > window.innerHeight
    );
  }

  elements.forEach((element, index) => {
    // Skip musical notation elements
    if (shouldExcludeElement(element)) {
      excludedCount++;
      return;
    }
    
    const rect = element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(element);
    if (rect.width > 0 && rect.height > 0) { // Only log visible elements
      elementData.push({
        index: index,
        tag: element.tagName,
        classList: Array.from(element.classList),
        id: element.id || '',
        ancestry: getAncestry(element),
        documentTop: rect.top + scrollY,
        documentLeft: rect.left + scrollX,
        viewportTop: rect.top,
        viewportLeft: rect.left,
        width: rect.width,
        height: rect.height,
        computedStyles: {
          backgroundColor: computedStyle.backgroundColor,
          backgroundImage: computedStyle.backgroundImage !== 'none' ? computedStyle.backgroundImage : 'none',
          color: computedStyle.color,
          fontSize: computedStyle.fontSize,
          display: computedStyle.display,
          opacity: computedStyle.opacity,
          visibility: computedStyle.visibility
        },
        renderStatus: {
          isVisible: computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden' && computedStyle.opacity !== '0',
          isOffScreen: isOffScreen(element)
        }
      });
    }
  });

  const dataToStore = {
    timestamp,
    pageUrl,
    viewport: { width: viewportWidth, height: viewportHeight },
    scroll: { x: scrollX, y: scrollY },
    elements: elementData,
    summary: {
      totalElements: elements.length,
      excludedElements: excludedCount,
      capturedElements: elementData.length
    }
  };

  sessionStorage.setItem('elementMapperData', JSON.stringify(dataToStore));
  console.log(`ElementMapper: Captured detailed data for ${elementData.length} visible elements (excluded ${excludedCount} musical notation elements) at ${timestamp}`);
  trackFeatureUsage('element_mapper', 'capture');
};

/**
 * Exports the element map data from sessionStorage as a JSON file for download.
 * @returns {Object} The element map data
 */
export function exportElementMap() {
  const mapDataStr = sessionStorage.getItem('elementMapperData');
  if (!mapDataStr) {
    console.error('ElementMapper: No data found in sessionStorage to export.');
    return null;
  }

  const mapData = JSON.parse(mapDataStr);
  const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(mapData, null, 2));
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', dataStr);
  downloadAnchorNode.setAttribute('download', `elementMap_${new Date().toISOString().replace(/[:.]/g, '-')}.json`);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
  console.log('ElementMapper: Exported element map data to JSON file.');
  trackFeatureUsage('element_mapper', 'export');
  return mapData;
} 